import { describeClientError } from '@src/app/shared/utils/client-error-message';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Roles } from '@app/core/guard/Roles';
import { Permissions } from '@app/core/guard/Permissions';
import { environment } from '@env/environment';
import {
  PartnerReferralItemResponse,
  PartnerReferralOverviewResponse,
} from '@interfaces/partners/responses/partner-referral.response';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnerReferralsHttpService } from '@services/http/partners/partner-referrals-http.service';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import {
  ReferralShareChannel,
  buildReferralLink,
  buildShareUrl,
  referralChannelLabel,
  referralDetail,
  referralStatusLabel,
} from '@src/app/shared/utils/partner-referral-link.utils';

/** Tempo que o botão fica em "Copiado ✓", como no protótipo. */
const COPIED_FEEDBACK_MS = 2500;

/** Um dos três cartões de números, com o ícone e a cor do ícone. */
interface ReferralKpi {
  label: string;
  value: number;
  icon: string;
  tone: 'navy' | 'blue' | 'brand';
}

/**
 * Comercial › Indique e ganhe (card 3461).
 *
 * O usuário do parceiro vê o próprio link de indicação, compartilha (copiar, WhatsApp, LinkedIn,
 * E-mail) e acompanha quem se cadastrou por ele. O master (`COMMERCIAL_ALL_DEALS`) vê as
 * indicações do time inteiro, com a coluna "Indicado por".
 *
 * Visual da opção A da rodada 2, escolhida pelo Breno em 28/10/2026 (card 3765; protótipo
 * `indique-e-ganhe-opcoes-de-visual-card-3461#v2-a` no acervo): bloco navy com a regra dos 10%, o link e o
 * compartilhar; os três números em cartões com ícone; a lista na largura toda.
 *
 * A regra de ganho (10% da receita da Legatus no primeiro deal do indicado) aparece como texto desde o card
 * 3765, por decisão do Breno em 28/10/2026, revendo a de 16/10/2026. Continua sem coluna Bônus nem "Bônus
 * acumulado": o valor não é calculado em lugar nenhum até existir o módulo de Comissões.
 *
 * A rota é alcançável por URL direta por um usuário interno (o grupo FULL do TENANT recolhe os
 * claims de `CommercialPermissions`), e o interno não tem link. Mesmo remédio de Comercial ›
 * Usuários: a tela checa o papel e, fora do PARTNER, só mostra um aviso.
 */
@Component({
  selector: 'app-referral',
  standalone: true,
  imports: [CommonModule, SharedModule, VaultDateTimePipe],
  templateUrl: './referral.component.html',
  styleUrls: ['./referral.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralComponent implements OnInit {
  private readonly referralsService = inject(PartnerReferralsHttpService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  readonly breadcrumbs: MenuItem[] = [
    { label: 'Comercial' },
    { label: 'Indique e ganhe', routerLink: '/dashboard/commercial/referral' },
  ];
  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  readonly isPartner = signal<boolean | null>(null);
  readonly isMaster = signal(false);
  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly overview = signal<PartnerReferralOverviewResponse | null>(null);
  readonly copied = signal(false);

  readonly referralLink = computed(() => {
    const code = this.overview()?.code;
    return code ? buildReferralLink(environment.partnerLandingUrl, code) : '';
  });

  readonly referrals = computed<PartnerReferralItemResponse[]>(
    () => this.overview()?.referrals ?? []
  );

  /**
   * Colunas da lista. "Indicado por" só para o master, que vê as indicações da equipe; o usuário comum só vê as
   * dele. O app-table liga o template pelo nome do CAMPO (`appNamedTemplate` = `field`), não pela propriedade
   * `template`.
   */
  readonly columnDefinitions = computed<ColumnDefinition[]>(() => [
    new ColumnDefinition({ field: 'partnerName', header: 'Parceiro indicado' }),
    ...(this.isMaster()
      ? [new ColumnDefinition({ field: 'referredByUserName', header: 'Indicado por' })]
      : []),
    new ColumnDefinition({ field: 'channel', header: 'Via' }),
    new ColumnDefinition({ field: 'referredAt', header: 'Quando' }),
    new ColumnDefinition({ field: 'status', header: 'Status' }),
  ]);

  /** Os três cartões de números (opção A da rodada 2, card 3765). */
  readonly kpis = computed<ReferralKpi[]>(() => {
    const overview = this.overview();
    return [
      {
        label: 'Cliques no link',
        value: overview?.clicks ?? 0,
        icon: 'pi pi-arrow-up-right',
        tone: 'navy',
      },
      {
        label: 'Parceiros cadastrados',
        value: overview?.signUps ?? 0,
        icon: 'pi pi-user-plus',
        tone: 'blue',
      },
      {
        label: 'Indicados que já registraram deal',
        value: overview?.dealsGenerated ?? 0,
        icon: 'pi pi-briefcase',
        tone: 'brand',
      },
    ];
  });

  readonly channelLabel = referralChannelLabel;
  readonly statusLabel = referralStatusLabel;
  readonly detail = referralDetail;

  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
    });

    this.permissionsService
      .hasPermission(Permissions.Commercial.ALL_DEALS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isMaster => this.isMaster.set(isMaster));

    this.permissionsService
      .hasRole(Roles.PARTNER)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isPartner => {
        const wasPartner = this.isPartner();
        this.isPartner.set(isPartner);
        // Só busca para parceiro, e uma vez: o interno não tem link de indicação.
        if (isPartner && !wasPartner) this.load();
      });
  }

  load(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.referralsService.getMyReferral().subscribe({
      next: response => {
        this.overview.set(response);
        this.loading.set(false);
        if (!response?.code) {
          this.errorMessage.set(
            'Não recebemos o seu link de indicação. Clique em Tentar de novo; se continuar assim, fale com o time da Legatus.'
          );
        }
      },
      error: error => {
        this.loading.set(false);
        this.errorMessage.set(
          describeClientError(
            error,
            'Não conseguimos carregar o seu link de indicação agora. Confira a sua conexão e clique em Tentar de novo.'
          )
        );
      },
    });
  }

  copyLink(): void {
    const link = this.referralLink();
    if (!link) return;

    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (!clipboard) {
      this.showCopyFailure();
      return;
    }

    clipboard
      .writeText(link)
      .then(() => {
        this.copied.set(true);
        if (this.copiedTimer) clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => this.copied.set(false), COPIED_FEEDBACK_MS);
      })
      .catch(() => this.showCopyFailure());
  }

  share(channel: ReferralShareChannel): void {
    const link = this.referralLink();
    if (!link || typeof window === 'undefined') return;

    const url = buildShareUrl(link, channel);
    if (channel === 'email') {
      // mailto abre o programa de e-mail; numa aba nova ficaria uma aba em branco para trás.
      window.location.href = url;
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private showCopyFailure(): void {
    this.toast.showToastError(
      'Não foi possível copiar o link',
      'O navegador bloqueou a cópia automática. Clique no link no quadro acima para selecioná-lo e copie com Ctrl+C.'
    );
  }
}
